import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    ArrowLeft, Calendar, Check, Layers, Monitor, Tag, Compass, Code2,
} from 'lucide-react';
import { useApp } from '../hooks/useApps';
import AppGallery from '../components/apps/AppGallery';
import AppActions from '../components/apps/AppActions';
import AppGrid from '../components/apps/AppGrid';
import EditAppButton from '../components/apps/EditAppButton';
import EditModeGate from '../components/admin/EditModeGate';
import Badge from '../components/ui/Badge';
import StatusBadge from '../components/ui/StatusBadge';
import Spinner from '../components/ui/Spinner';
import EmptyState from '../components/ui/EmptyState';
import { formatMonth } from '../utils/format';
import { SITE } from '../config/site';
import { trackEvent } from '../utils/analytics';

const InfoRow = ({ icon: Icon, label, children }) => (
    <div className="flex items-start gap-3 py-3 border-b border-slate-200/60 dark:border-white/[0.06] last:border-0">
        <Icon className="w-4 h-4 text-slate-400 dark:text-slate-500 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
            <div className="text-xs text-slate-400 dark:text-slate-500 mb-0.5">{label}</div>
            <div className="text-sm text-slate-800 dark:text-slate-100">{children}</div>
        </div>
    </div>
);

/**
 * Trang giới thiệu một ứng dụng — nơi người dùng "hạ cánh" khi bấm vào thẻ ở
 * trang chủ: ảnh chụp màn hình, mô tả, tính năng, công nghệ và các liên kết
 * (mở ứng dụng, GitHub, tải về...). Link ngoài chỉ mở khi người dùng chủ động bấm.
 */
const AppDetail = () => {
    const { id } = useParams();
    const { app, related, loading, upsertApp } = useApp(id);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    useEffect(() => {
        if (!app) return undefined;
        document.title = `${app.title} — ${SITE.shortName}`;
        trackEvent('app_detail_view', 'apps', app.title);
        return () => {
            document.title = SITE.name;
        };
    }, [app]);

    if (loading && !app) {
        return (
            <div className="flex justify-center py-32">
                <Spinner size="lg" />
            </div>
        );
    }

    if (!app) {
        return (
            <div className="max-w-2xl mx-auto px-6 py-24">
                <EmptyState
                    icon={Compass}
                    title="Không tìm thấy ứng dụng"
                    message="Ứng dụng này có thể đã đổi tên, bị gỡ, hoặc đang được ẩn."
                    action={
                        <Link
                            to="/"
                            className="btn-accent"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Về trang chủ
                        </Link>
                    }
                />
            </div>
        );
    }

    return (
        <EditModeGate onSaved={upsertApp}>
            <div className="relative">
                <div className="absolute inset-x-0 top-0 h-[420px] overflow-hidden pointer-events-none">
                    <div className="absolute -top-20 left-1/4 w-96 h-96 bg-slate-300/25 dark:bg-slate-600/15 rounded-full blur-3xl animate-blob" />
                    <div className="absolute top-10 right-1/4 w-80 h-80 bg-slate-400/15 dark:bg-slate-500/10 rounded-full blur-3xl animate-blob-reverse animation-delay-2000" />
                </div>

                <div className="relative max-w-6xl mx-auto px-6 py-10 md:py-14">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors mb-8"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Tất cả ứng dụng
                    </Link>

                    <header className="mb-10">
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                            <Badge>{app.category}</Badge>
                            {app.status !== 'live' && <StatusBadge status={app.status} />}
                            {app.featured && <Badge variant="accent">Nổi bật</Badge>}
                            <EditAppButton app={app} variant="pill" className="ml-auto" />
                        </div>

                        <h1 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-3 text-balance">
                            {app.title}
                        </h1>

                        {app.tagline && (
                            <p className="text-lg md:text-xl text-slate-500 dark:text-slate-400 max-w-3xl leading-relaxed">
                                {app.tagline}
                            </p>
                        )}
                    </header>

                    {/* Thứ tự DOM chính là thứ tự trên điện thoại: ảnh → nút bấm → nội dung.
                        Desktop đặt lại bằng col-start/row-start để vẫn là hai cột.
                        Nếu để nút trong <aside> ở cuối DOM, trên điện thoại người dùng
                        phải cuộn qua cả phần tính năng mới thấy nút "Mở ứng dụng". */}
                    <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start gap-x-10 gap-y-8">
                        <div className="lg:col-start-1 lg:row-start-1 min-w-0">
                            <AppGallery app={app} />
                        </div>

                        <aside className="lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-28 space-y-6">
                            <div className="p-5 bg-white/70 dark:bg-white/[0.05] backdrop-blur-glass border border-white/50 dark:border-white/[0.08] rounded-2xl shadow-lg shadow-slate-900/5 dark:shadow-black/20">
                                <AppActions app={app} />
                            </div>

                            <div className="px-5 py-2 bg-white/60 dark:bg-white/[0.04] backdrop-blur-sm border border-white/50 dark:border-white/[0.08] rounded-2xl">
                                <InfoRow icon={Layers} label="Danh mục">
                                    {app.category}
                                </InfoRow>
                                <InfoRow icon={Monitor} label="Nền tảng">
                                    {app.platform}
                                </InfoRow>
                                {app.date && (
                                    <InfoRow icon={Calendar} label="Thời điểm">
                                        {formatMonth(app.date)}
                                    </InfoRow>
                                )}
                                {app.tags.length > 0 && (
                                    <InfoRow icon={Tag} label="Thẻ">
                                        <div className="flex flex-wrap gap-1.5 mt-1">
                                            {app.tags.map((tag) => (
                                                <Badge key={tag} variant="subtle">
                                                    #{tag}
                                                </Badge>
                                            ))}
                                        </div>
                                    </InfoRow>
                                )}
                            </div>
                        </aside>

                        <div className="lg:col-start-1 lg:row-start-2 space-y-10 min-w-0">
                            <section>
                                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Ứng dụng này là gì</h2>
                                <div className="space-y-4 text-slate-600 dark:text-slate-300 leading-relaxed">
                                    {(app.longDescription || app.description)
                                        .split('\n')
                                        .filter((para) => para.trim())
                                        .map((para, i) => (
                                            <p key={i}>{para}</p>
                                        ))}
                                </div>
                            </section>

                            {app.features.length > 0 && (
                                <section>
                                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                                        Làm được những gì
                                    </h2>
                                    <ul className="grid sm:grid-cols-2 gap-3">
                                        {app.features.map((feature) => (
                                            <li
                                                key={feature}
                                                className="flex items-start gap-3 p-4 bg-white/60 dark:bg-white/[0.05] backdrop-blur-sm border border-white/50 dark:border-white/[0.08] rounded-xl"
                                            >
                                                <span className="w-5 h-5 shrink-0 rounded-full bg-slate-900 dark:bg-white flex items-center justify-center mt-0.5">
                                                    <Check className="w-3 h-3 text-white dark:text-slate-900" />
                                                </span>
                                                <span className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                                                    {feature}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {app.techStack.length > 0 && (
                                <section>
                                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                                        Xây dựng bằng
                                    </h2>
                                    <div className="flex flex-wrap gap-2">
                                        {app.techStack.map((tech) => (
                                            <span
                                                key={tech}
                                                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white/60 dark:bg-white/[0.06] border border-white/50 dark:border-white/[0.08] rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200"
                                            >
                                                <Code2 className="w-3.5 h-3.5 text-slate-400" />
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </div>

                    </div>

                    {related.length > 0 && (
                        <section className="mt-20 pt-12 border-t border-slate-200/60 dark:border-white/[0.06]">
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-8">
                                Cùng nhóm với ứng dụng này
                            </h2>
                            <AppGrid apps={related} />
                        </section>
                    )}
                </div>
            </div>
        </EditModeGate>
    );
};

export default AppDetail;
